import React, { useEffect, useState, useRef } from "react";
import moment from "moment";
import striptags from "striptags";
import Link from "next/link";
import Image from "next/image";

const Pagination = ({ currentPage, totalPages, handlePageChange }) => {
  if (totalPages <= 1) return null;

  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 3;

    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);

      if (currentPage > 3) {
        pages.push("...");
      }

      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);

      for (let i = start; i <= end; i++) {
        if (!pages.includes(i)) pages.push(i);
      }

      if (currentPage < totalPages - 2) {
        pages.push("...");
      }

      if (!pages.includes(totalPages)) pages.push(totalPages);
    }
    return pages;
  };

  const pages = getPageNumbers();

  return (
    <div className="article-pagination">
      <nav aria-label="...">
        <ul className="pagination">
          <li className="page-item">
            <Link
              className="page-link"
              href="#!"
              tabIndex="-1"
              aria-disabled={currentPage === 1}
              onClick={(e) => {
                e.preventDefault();
                handlePageChange(currentPage - 1);
              }}
            >
              Prev
            </Link>
          </li>
          {pages.map((page, i) => (
            <li
              key={i}
              className={`page-item ${page === currentPage ? "active" : ""} ${page === "..." ? "disabled" : ""
                }`}
            >
              {page === "..." ? (
                <span className="page-link">...</span>
              ) : (
                <Link
                  className="page-link"
                  href="#!"
                  onClick={(e) => {
                    e.preventDefault();
                    handlePageChange(page);
                  }}
                >
                  {page}
                </Link>
              )}
            </li>
          ))}
          <li className="page-item">
            <Link
              className="page-link"
              href="#!"
              aria-disabled={currentPage === totalPages}
              onClick={(e) => {
                e.preventDefault();
                handlePageChange(currentPage + 1);
              }}
            >
              Next
            </Link>
          </li>
        </ul>
      </nav>
    </div>
  );
};

const AllArticle = ({ articleData, categoryData }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("all");
  const [showFilter, setShowFilter] = useState(false);
  const filterRef = useRef(null);
  const [filterCaseData, setFilterCaseData] = useState([]);
  const itemsPerPage = 6;

  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = currentPage * itemsPerPage;
  const visibleItems =
    filterCaseData.length > 0
      ? filterCaseData && filterCaseData?.slice(startIndex, endIndex)
      : articleData && articleData?.results?.slice(startIndex, endIndex);

  const totalItems = filterCaseData.length > 0
    ? filterCaseData.length
    : (articleData && articleData?.results?.length) || 0;

  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const searchData = () => {
    const searchResult = articleData?.results.filter((blog) => {
      const matchesCategory = blog.category.toLowerCase().includes(searchInput?.toLowerCase());
      const matchesSearchTerm = (blog.title || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
        (striptags(blog.description || "")).toLowerCase().includes(searchTerm.toLowerCase());
      return matchesCategory && matchesSearchTerm;
    });
    setFilterCaseData(searchResult);
    setCurrentPage(1);
  };

  useEffect(() => {
    articleData && searchData();
  }, [searchInput, searchTerm]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterRef.current && !filterRef.current.contains(event.target)) {
        setShowFilter(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleCategorySelect = (category, index) => {
    if (category === "all") {
      setSearchInput("");
      setActiveTab("all");
    } else {
      setSearchInput(category.name);
      setActiveTab(index);
    }
    setCurrentPage(1);
    setShowFilter(false);
  };

  return (
    <section className="all-article-section">
      <div className="all-article-inner">
        <div className="container">
          <div className="article-search-wrap">
            <div className="search-box">
              <input
                type="text"
                placeholder="Search articles..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="search-icon"
              >
                <path
                  d="M19 19L14.65 14.65M17 9C17 13.4183 13.4183 17 9 17C4.58172 17 1 13.4183 1 9C1 4.58172 4.58172 1 9 1C13.4183 1 17 4.58172 17 9Z"
                  stroke="#565656"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <div className="filter-dropdown-wrap" ref={filterRef}>
              <button
                className={`filter-btn ${showFilter ? "active" : ""}`}
                onClick={() => setShowFilter(!showFilter)}
                aria-label="Filter by category"
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M3 6H21M6 12H18M10 18H14"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              {showFilter && (
                <div className="filter-dropdown-menu">
                  <ul>
                    <li
                      className={activeTab === "all" ? "active" : ""}
                      onClick={() => handleCategorySelect("all")}
                    >
                      All Articles
                    </li>
                    {categoryData?.map((cat, i) => (
                      <li
                        key={i}
                        className={activeTab === i ? "active" : ""}
                        onClick={() => handleCategorySelect(cat, i)}
                      >
                        {cat.name}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
          <div className="row desktop-row">
            <div className="col-lg-4 col-md-4">
              <div className="all-article-tag">
                <div
                  className="nav flex-column nav-pills me-3"
                  id="v-pills-tab"
                  role="tablist"
                  aria-orientation="vertical"
                >
                  <button
                    className={`nav-link ${activeTab === "all" ? "active" : ""
                      }`}
                    id="v-pills-home-tab"
                    data-bs-toggle="pill"
                    data-bs-target="#v-pills-home"
                    type="button"
                    role="tab"
                    aria-controls="v-pills-home"
                    aria-selected="true"
                    onClick={() => {
                      setSearchInput("");
                      setActiveTab("all");
                    }}
                  >
                    All Articles
                  </button>
                  {categoryData &&
                    categoryData?.map((val, index) => {
                      return (
                        <button
                          key={index}
                          className={`nav-link ${activeTab == index ? "active" : ""
                            }`}
                          id={index}
                          data-bs-toggle="pill"
                          data-bs-target={`#${index}`}
                          type="button"
                          role="tab"
                          aria-controls={index}
                          aria-selected="false"
                          onClick={() => {
                            setSearchInput(val?.name);
                            setActiveTab(index);
                          }}
                        >
                          {val?.name}
                        </button>
                      );
                    })}
                </div>
              </div>
            </div>
            <div className="col-lg-8 col-md-8">
              <div className="all-article-tab-content">
                <div className="tab-content" id="v-pills-tabContent">
                  <div
                    className="tab-pane fade show active"
                    id="v-pills-home"
                    role="tabpanel"
                    aria-labelledby="v-pills-home-tab"
                  >
                    <div className="articles-box">
                      {filterCaseData.length === 0 ? (
                        <p>No articles found for the selected category.</p>
                      ) : (
                        <div className="row">
                          {visibleItems &&
                            visibleItems.map((val, index) => {
                              const formattedDate = moment(
                                val?.date_published
                              ).format("MMMM Do, YYYY");
                              return (
                                <div
                                  className="col-lg-6 col-md-6 col-sm-6"
                                  key={index}
                                >
                                  <Link href={`/blog/${val?.slug}`}>
                                    <div className="latest-article-card">
                                      <div className="latest-article-img">
                                        <Image
                                          src={val?.image?.url}
                                          alt={val?.image?.alt_text ?? 'articalimg'}
                                          height={500}
                                          width={500}
                                          loading="eager"
                                        />
                                        <span className="computing-link">
                                          {val?.category}
                                        </span>
                                      </div>
                                      <div className="latest-article-content">
                                        <span className="article-date-time">
                                          {formattedDate}
                                          <span className="article-time">{val?.reading_times}</span>
                                        </span>
                                        <span className="title-text">{val?.title}</span>
                                        <p className="description-text">
                                          {striptags(val?.description)}
                                        </p>
                                      </div>
                                    </div>
                                  </Link>
                                </div>
                              );
                            })}
                        </div>
                      )}
                    </div>
                    <div className="row">
                      <div className="col-lg-12">
                        {filterCaseData.length === 0 ? (
                          " "
                        ) : (
                          <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            handlePageChange={handlePageChange}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                  <div
                    className="tab-pane fade"
                    id="v-pills-profile"
                    role="tabpanel"
                    aria-labelledby="v-pills-profile-tab"
                  >
                    ...
                  </div>
                  <div
                    className="tab-pane fade"
                    id="v-pills-messages"
                    role="tabpanel"
                    aria-labelledby="v-pills-messages-tab"
                  >
                    ...
                  </div>
                  <div
                    className="tab-pane fade"
                    id="v-pills-settings"
                    role="tabpanel"
                    aria-labelledby="v-pills-settings-tab"
                  >
                    ...
                  </div>
                  <div
                    className="tab-pane fade"
                    id="v-pills-Industries"
                    role="tabpanel"
                    aria-labelledby="v-pills-Industries-tab"
                  >
                    ...
                  </div>
                </div>
              </div>
            </div>
          </div>


          <div className="mobile-articles-list">
            <div className="all-article-tab-content">
              <div className="articles-box">
                <div className="row">
                  {visibleItems?.length === 0 ? (
                    <div className="col-12">
                      <p>No articles found for the selected criteria.</p>
                    </div>
                  ) : (
                    <div className="row">
                      {visibleItems &&
                        visibleItems.map((val, index) => {
                          const formattedDate = moment(
                            val?.date_published
                          ).format("MMMM Do, YYYY");
                          return (
                            <div
                              className="col-lg-6 col-md-6 col-sm-6"
                              key={index}
                            >
                              <Link href={`/blog/${val?.slug}`}>
                                <div className="latest-article-card">
                                  <div className="latest-article-img">
                                    <Image
                                      src={val?.image?.url}
                                      alt={val?.image?.alt_text ?? 'articalimg'}
                                      height={500}
                                      width={500}
                                      loading="eager"
                                    />
                                    <span className="computing-link">
                                      {val?.category}
                                    </span>
                                  </div>
                                  <div className="latest-article-content">
                                    <span className="article-date-time">
                                      {formattedDate}
                                      <span className="article-time">{val?.reading_times}</span>
                                    </span>
                                    <span className="title-text">{val?.title}</span>
                                    <p className="description-text">
                                      {striptags(val?.description)}
                                    </p>
                                  </div>
                                </div>
                              </Link>
                            </div>
                          );
                        })}
                    </div>
                  )}
                </div>
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  handlePageChange={handlePageChange}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AllArticle;
